import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { Dialog } from './shared/Dialog';

interface InvoiceSaveSuccessDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onNewInvoice: () => void;
}

export const InvoiceSaveSuccessDialog: React.FC<InvoiceSaveSuccessDialogProps> = ({
  isOpen,
  onClose,
  onNewInvoice
}) => {
  const { t } = useTranslation();

  const handleNewInvoice = () => {
    onClose();
    onNewInvoice();
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} size="md">
      <div className="bg-white rounded-3xl border shadow-lg overflow-hidden">
        <div className="px-8 py-10 text-center">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.3 }}
            className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-6 h-6 text-green-600" />
          </motion.div>

          <motion.h2 
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            className="text-2xl font-semibold text-gray-900 mb-3"
          >
            {t('dialog.saveSuccess.title')}
          </motion.h2>

          <motion.p 
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.3 }}
            className="text-gray-600 text-lg mb-8"
          >
            {t('dialog.saveSuccess.message')}
          </motion.p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.3 }}
              onClick={onClose}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {t('dialog.saveSuccess.viewInvoice')}
            </motion.button>

            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.3 }}
              onClick={handleNewInvoice}
              className="btn-primary"
            >
              {t('dialog.saveSuccess.newInvoice')}
            </motion.button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
